import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  ContentCopyOutlined,
  GroupsOutlined,
  LinkOutlined,
  ShareOutlined,
  TokenOutlined,
  WhatsApp,
} from "@mui/icons-material";
import { GlobalContext } from "../../context/GlobalContext";
import { buildReferralLink } from "../../utils/referralAttribution";

const Affiliate = ({ lang }) => {
  const theme = useTheme();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    const res = await hitAxios({
      path: "/api/affiliate/me",
      post: false,
      admin: false,
      showLoading: false,
    });
    setLoading(false);
    if (res?.data?.success) setData(res.data.data);
  }, [hitAxios]);

  React.useEffect(() => {
    load();
  }, [load]);

  const code = data?.referral_code || "";
  const link = code ? buildReferralLink(code) : "";
  const reward = data?.signup_credits ?? 25;
  const refereeBonus = data?.referee_bonus ?? 0;

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showSnack(label || lang?.copied || "Copied!", true);
    } catch {
      showSnack(lang?.copyFailed || "Could not copy", false);
    }
  };

  const shareNative = async () => {
    const text =
      lang?.affiliateShareText ||
      `Join me on Beyund AI — create ads, images & video with AI. Use my invite link and I earn ${reward} credits when you sign up:\n${link}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Beyund AI", text, url: link });
        return;
      } catch {
        /* fall through */
      }
    }
    copy(text, lang?.shareCopied || "Share message copied");
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(
    `Join me on Beyund AI! Sign up with my link and start creating:\n${link}`,
  )}`;

  if (loading && !data) {
    return (
      <Typography color="text.secondary">
        {lang?.loading || "Loading…"}
      </Typography>
    );
  }

  if (data && data.enabled === false) {
    return (
      <Card
        elevation={0}
        sx={{ p: 3, border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <Typography variant="h6" fontWeight={800}>
          {lang?.affiliateNav || "Affiliate"}
        </Typography>
        <Typography color="text.secondary" mt={1}>
          {lang?.affiliateDisabled ||
            "The affiliate program is currently paused. Check back soon."}
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
        <GroupsOutlined color="primary" sx={{ fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={800}>
            {lang?.affiliateTitle || "Invite & earn credits"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {lang?.affiliateSubtitle ||
              `Share your unique link. When a friend creates an account, you earn ${reward} credits.`}
          </Typography>
        </Box>
      </Stack>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ my: 3 }}
      >
        {[
          {
            label: lang?.affiliateInvites || "Successful invites",
            value: data?.total_invites ?? 0,
            icon: <GroupsOutlined />,
          },
          {
            label: lang?.affiliateCreditsEarned || "Credits earned",
            value: data?.total_credits_earned ?? 0,
            icon: <TokenOutlined />,
          },
          {
            label: lang?.affiliatePerSignup || "Per signup",
            value: `+${reward}`,
            icon: <LinkOutlined />,
          },
        ].map((s) => (
          <Card
            key={s.label}
            elevation={0}
            sx={{
              flex: 1,
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
              <Box sx={{ color: "primary.main", display: "flex" }}>{s.icon}</Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {s.label}
              </Typography>
            </Stack>
            <Typography variant="h4" fontWeight={800}>
              {s.value}
            </Typography>
          </Card>
        ))}
      </Stack>

      <Card
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
          {lang?.affiliateYourLink || "Your invite link"}
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={link}
          InputProps={{
            readOnly: true,
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={lang?.copy || "Copy"}>
                  <IconButton onClick={() => copy(link)} edge="end" size="small">
                    <ContentCopyOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1.5 }}
        />
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Chip
            label={`${lang?.affiliateCode || "Code"}: ${code}`}
            onClick={() => copy(code, lang?.codeCopied || "Code copied")}
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
          <Button
            variant="contained"
            startIcon={<ShareOutlined />}
            onClick={shareNative}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {lang?.share || "Share"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<WhatsApp />}
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            WhatsApp
          </Button>
          <Button
            variant="text"
            startIcon={<ContentCopyOutlined />}
            onClick={() => copy(link)}
            sx={{ textTransform: "none" }}
          >
            {lang?.copyLink || "Copy link"}
          </Button>
        </Stack>
      </Card>

      <Card
        elevation={0}
        sx={{
          p: 2.5,
          mb: 3,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle2" fontWeight={700} mb={1}>
          {lang?.affiliateHowItWorks || "How it works"}
        </Typography>
        <Stack spacing={1.25} divider={<Divider flexItem />}>
          {[
            lang?.affiliateStep1 ||
              "Copy your unique link and share it with creators, clients, or your audience.",
            lang?.affiliateStep2 ||
              `They sign up via your link. You earn ${reward} credits instantly.`,
            refereeBonus > 0
              ? lang?.affiliateStep3Bonus ||
                `Your friend also gets +${refereeBonus} bonus credits on top of signup credits.`
              : lang?.affiliateStep3 ||
                "Credits appear in your balance and in Usage — invite more to keep earning.",
          ].map((step, i) => (
            <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
              <Chip
                size="small"
                label={i + 1}
                color="primary"
                sx={{ fontWeight: 800, minWidth: 28 }}
              />
              <Typography variant="body2" color="text.secondary">
                {step}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Card>

      <Card
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {lang?.affiliateHistory || "Invite history"}
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{lang?.invitee || "Invitee"}</TableCell>
                <TableCell align="right">{lang?.credits || "Credits"}</TableCell>
                <TableCell>{lang?.date || "Date"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.recent || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {lang?.affiliateEmptyHistory ||
                        "No invites yet — share your link to start earning."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                (data.recent || []).map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.referred_email}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      +{row.credits_awarded}
                    </TableCell>
                    <TableCell>
                      {row.created_at
                        ? new Date(row.created_at).toLocaleString()
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
};

export default Affiliate;
